import React, { useEffect, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequestWithRetry } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { lossAversionModalQuery } from '@atlassian/jira-relay/src/__generated__/lossAversionModalQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useTrialDetails } from '../../services/check-experiment-eligibility/useTrialDetails.tsx';
import { SplitPanelModal } from './split-panel/index.tsx';
import { StorageModal } from './storage/index.tsx';
import { UserLimitModal } from './user-limit/index.tsx';

export const JiraLossAversionModal = () => (
	<JSErrorBoundary
		id="jiraLossAversionModal"
		packageName="jiraLossAversion"
		teamName="luna-growth"
		fallback="unmount"
	>
		<div data-testid="loss-aversion.ui.loss-aversion-modal.jira-loss-aversion-modal-content">
			<JiraLossAversionModalContent />
		</div>
	</JSErrorBoundary>
);
const GB_IN_BYTES = 1_000_000_000;

const bytesToGB = (bytes: number) => bytes / GB_IN_BYTES;

export const JiraLossAversionModalContent = () => {
	const cloudId = useCloudId();
	const { billingSourceSystem } = useTrialDetails();
	const isSiteAdmin = useIsSiteAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const shouldLoadHamsUserCount =
		billingSourceSystem !== 'CCP' && isSiteAdmin && fg('jira_loss_aversion_standard_hams');

	const [hamsUserCount, setHamsUserCount] = useState<number>(1);
	const [loadingHamsUserCount, setLoadingHamsUserCount] = useState(shouldLoadHamsUserCount);
	const [hamsUserCountError, setHamsUserCountError] = useState(null);

	const data = useLazyLoadQuery<lossAversionModalQuery>(
		graphql`
			query lossAversionModalQuery($cloudId: ID!, $isCCP: Boolean!) {
				jira {
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: JIRA_SOFTWARE)
				}
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "jira-software.ondemand") {
						entitlement {
							subscription {
								accountDetails {
									invoiceGroup {
										experienceCapabilities {
											configurePayment {
												experienceUrl
												isAvailableToUser
											}
										}
									}
								}
							}
						}
						... on CommerceEntitlementInfoCcp @include(if: $isCCP) {
							__typename
							entitlement {
								subscription {
									chargeDetails {
										chargeQuantities {
											quantity
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId, isCCP: billingSourceSystem === 'CCP' },
	);

	useEffect(() => {
		const fetchData = async () => {
			if (!shouldLoadHamsUserCount) {
				return;
			}

			try {
				const response = await performGetRequestWithRetry(
					`/gateway/api/billing-ux/cloud/${cloudId}/billing/entitlement-group-detail`,
				);

				fireOperationalAnalytics(createAnalyticsEvent({}), 'fetched hamsUserCount', {});

				const jiraEntitlement = response.entitlements.find(
					(entitlement: Record<string, unknown>) =>
						entitlement.productKey === 'jira-software.ondemand',
				);

				const usage = jiraEntitlement?.usages?.find(
					(usages: Record<string, unknown>) => usages.unitType === 'USER',
				);

				setHamsUserCount(usage?.unitCount || 1);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'fetchHamsUserCountWithRetry',
						packageName: 'jiraLossAversion',
					},
				});

				setHamsUserCountError(error.message || 'Unknown HAMS user count fetch error');
			} finally {
				setLoadingHamsUserCount(false);
			}
		};

		fetchData();
	}, [
		cloudId,
		shouldLoadHamsUserCount,
		setLoadingHamsUserCount,
		setHamsUserCount,
		createAnalyticsEvent,
	]);

	const entitlementInfo = data?.tenantContexts?.[0]?.entitlementInfo;

	const entitlement = entitlementInfo?.entitlement;
	const configurePayment =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePayment;

	// This might come back as null from graphql but we want to default to undefined to make the types easier to work with
	const upgradeUrl = configurePayment?.experienceUrl || '/admin/billing/applications';

	if (!configurePayment?.experienceUrl) {
		fireErrorAnalytics({
			meta: {
				id: 'paymentUrlUndefined',
				packageName: 'jiraLossAversion',
				teamName: 'luna-growth',
			},
			attributes: { isSiteAdmin, billingSourceSystem, cloudId },
		});
	}

	const userCanPay = !!configurePayment?.isAvailableToUser;

	const userCount =
		entitlementInfo?.__typename === 'CommerceEntitlementInfoCcp'
			? entitlementInfo.entitlement?.subscription?.chargeDetails?.chargeQuantities?.[0]?.quantity ||
				1
			: 1;

	if (hamsUserCountError) {
		// if we have an error fetching the HAMS user count, we should not show the modal
		// throwing here causes this error to get caught by the error boundary and the
		// component to unmount
		throw new Error(hamsUserCountError);
	}

	if (loadingHamsUserCount) {
		return <></>;
	}

	const relevantUserCount = billingSourceSystem === 'CCP' ? userCount : hamsUserCount;

	if (relevantUserCount > 10) {
		return (
			<UserLimitModal
				userCanPay={userCanPay}
				userCount={relevantUserCount}
				upgradeUrl={upgradeUrl}
			/>
		);
	}

	const attachmentStorageUsed = data?.jira?.attachmentStorageUsed || 0;
	const storageUsedInGB = bytesToGB(attachmentStorageUsed);

	if (attachmentStorageUsed > 2 * GB_IN_BYTES) {
		return (
			<StorageModal
				userCanPay={userCanPay}
				upgradeUrl={upgradeUrl}
				storageUsedInGB={storageUsedInGB}
			/>
		);
	}

	return <SplitPanelModal userCanPay={userCanPay} upgradeUrl={upgradeUrl} />;
};
